import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { logAbsence } from '@/lib/actions';
import { TechnicalDetails } from '@/lib/TechnicalDetails';
import { fromDateKey } from '@/lib/supabase';
import { formatDayKey } from '@/lib/dates';
import { useIsNarrow } from '@/lib/useMediaQuery';
import { color, font, radius, shadow } from '@/lib/theme';
import {
  loadClosureDaysBetween,
  unpaidWarningFor,
  type EmployeeDashboardRow,
} from '@/features/dashboard/data';

/**
 * Log an Absence — the port of the live portal's LogAbsence.html, backed by
 * the log_absence RPC (migration 0011). Same field set, same sub-line. Since
 * 0031 (audit B3, 2026-10-13) only PTO offers the four decisions (Record,
 * Approve, Deny, Pending); every other type is Record-only — unplanned
 * absences are facts, not requests — and the form says so instead of offering
 * a choice the RPC would refuse. Three deliberate differences from the live form:
 *
 *  - The employee dropdown comes from the dashboard's already-loaded roster,
 *    which RLS has scoped to this manager's location — no extra fetch, and no
 *    way to even offer an out-of-scope name.
 *  - The type list shows the clean enum spellings ('Tardy/Late'); the RPC also
 *    accepts the Sheet-era 'Tardy / Late', so nothing breaks either way.
 *  - 'Called Off' and 'Other Unplanned' are NOT offered (Scott, 2026-09-18:
 *    "drop other unplanned, drop call-off for now"). points_for() scores
 *    them identically to Sick and No-Call-No-Show, so the four labels only
 *    ever split one behaviour across overlapping names, and in nine months
 *    of data neither was picked once. The enum and log_absence() still
 *    accept both (removing an enum value needs a column rewrite, and
 *    history may hold either), so a row carrying one still renders in the
 *    calendar and Manage Records. Re-adding is one line in ABSENCE_TYPES.
 *
 * Every outcome renders the RPC's own message — 'Logged and recorded.',
 * 'Logged as Pending - it is now in the approval queue.', the Needs
 * Documentation flag, or a friendly refusal. The result banner stays up after
 * success so a manager logging several absences in a row keeps their bearings.
 */

const ABSENCE_TYPES = ['Sick', 'Tardy/Late', 'No-Call-No-Show', 'PTO'] as const;

const DECISIONS = [
  { value: 'Record', label: 'Record only (no approval needed)' },
  { value: 'Approve', label: 'Approve' },
  { value: 'Deny', label: 'Deny' },
  { value: 'Pending', label: 'Pending (decide later)' },
] as const;

type Decision = (typeof DECISIONS)[number]['value'];

type Banner = { kind: 'success' | 'error'; text: string; detail?: string } | null;

/**
 * Pre-P8 punch list, Tier 2: the submit button always read "Log Absence"
 * regardless of which decision was selected, so clicking it while Approve or
 * Deny was chosen carried none of that weight. One label per decision.
 *
 * Record gets no confirm() dialog (below) — three-way call (this session,
 * Gemini, and an adversarial reviewer converged here): Record is the
 * default, highest-frequency path, and its email is already disclosed
 * upfront in this form's own intro copy, so a dialog on every routine entry
 * would train managers to click through it on muscle memory, weakening the
 * dialog's actual safety value on Approve/Deny where a wrong click is far
 * more consequential (a firm decision email, not a low-key FYI). But Record
 * being the DEFAULT selection is a real "easiest thing to submit by
 * accident" risk — an employee picked wrong, or a manager who forgot to
 * change the dropdown, still fires that FYI with zero warning. Naming who
 * gets notified directly on the button (no extra click, read in passing
 * right before the click lands) covers that without the fatigue cost.
 */
function submitLabel(decision: Decision, employeeName: string, notifyEmployee: boolean): string {
  if (decision === 'Approve') return 'Approve & Log';
  if (decision === 'Deny') return 'Deny & Log';
  if (decision === 'Pending') return 'Log as Pending';
  // Record.
  if (!employeeName || !notifyEmployee) return 'Log Absence';
  const firstName = employeeName.trim().split(/\s+/)[0] ?? employeeName;
  return `Record & Notify ${firstName}`;
}

/**
 * An ADVISORY estimate of workdays in [start, end] for the on-the-spot
 * allotment warning below — never authoritative. `log_absence`'s own RPC
 * computes the real, counted value at submit time; this only decides
 * whether a heads-up is worth showing first. Weekends and shop closures
 * are excluded; the half-day-applies-to-the-whole-range simplification
 * matches the live system's own existing behavior (a separate, lower-
 * priority Tier 3 item, not something new introduced here).
 */
function estimatePtoDays(
  start: string,
  end: string,
  closures: Set<string>,
  amount: 'Full' | 'Half',
): number {
  const factor = amount === 'Half' ? 0.5 : 1;
  // fromDateKey() (lib/supabase.ts) — local Date parts only, never string
  // parsing (trap #1), same helper the rest of the app already trusts.
  const cur = fromDateKey(start);
  const stop = fromDateKey(end);
  let workdays = 0;
  while (cur <= stop) {
    const dow = cur.getDay(); // 0 Sun .. 6 Sat
    const key = `${cur.getFullYear()}-${String(cur.getMonth() + 1).padStart(2, '0')}-${String(cur.getDate()).padStart(2, '0')}`;
    if (dow !== 0 && dow !== 6 && !closures.has(key)) workdays += 1;
    cur.setDate(cur.getDate() + 1);
  }
  return workdays * factor;
}

export function LogAbsence({
  employees,
  notifyEmployee,
  onLogged,
}: {
  /** Pre-P8 punch list, Tier 2: the FULL dashboard row, not just the bare
      roster fields — the on-the-spot allotment warning below needs
      `ptoTotal`/`allotment`, which only exist on the assembled view model. */
  employees: EmployeeDashboardRow[];
  /** company_settings.notify_employee — whether Record's button should claim
      it will notify anyone. Defaults true upstream if settings failed to
      load; a stale "will notify" claim is a much smaller problem than a
      Dashboard that can't load at all over a missing settings row. */
  notifyEmployee: boolean;
  /** Called after a successful log so the dashboard data refreshes quietly. */
  onLogged: () => void;
}) {
  const [employeeName, setEmployeeName] = useState('');
  const [absenceType, setAbsenceType] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [lateHours, setLateHours] = useState('');
  const [doctorsNote, setDoctorsNote] = useState(false);
  const [decision, setDecision] = useState<Decision>('Record');
  const [ptoAmount, setPtoAmount] = useState<'Full' | 'Half'>('Full');
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState<Banner>(null);
  const [closures, setClosures] = useState<Set<string>>(new Set());
  const narrow = useIsNarrow();

  // Pre-P8 punch list, Tier 2: fetches the SAME closure_days() RPC the Team
  // Calendar already uses, so the on-the-spot allotment warning below can
  // estimate a NOT-YET-SUBMITTED entry's workday count without reimplementing
  // the closure calendar's own quirky rules client-side (CLAUDE.md: "ask the
  // database, don't reimplement"). Advisory only — a fetch failure just
  // means no warning shows, never a blocked form.
  useEffect(() => {
    // Loaded for every type once a date exists: the allotment warning needs it
    // for PTO, and the Needs Documentation hint counts WORKING days (0031, B4)
    // for the rest.
    if (!startDate) {
      setClosures(new Set());
      return;
    }
    let stale = false;
    loadClosureDaysBetween(startDate, endDate || startDate).then(
      (c) => {
        if (!stale) setClosures(c);
      },
      () => {
        if (!stale) setClosures(new Set());
      },
    );
    return () => {
      stale = true;
    };
  }, [startDate, endDate]);

  const empRow = employees.find((r) => r.employee.full_name === employeeName);
  const halfDayRange = ptoAmount === 'Half' && endDate !== '' && endDate > startDate;
  // Adversarial review, pre-P8 punch list: a multi-day Record with no
  // doctor's note doesn't actually land as 'Recorded' — log_absence() files
  // it as 'Needs Documentation' instead (the exact condition the hint just
  // below already covers), and absence_facts counts ZERO PTO days for that
  // status until the note resolves it. Showing the unpaid-allotment warning
  // in that case would state a false consequence right next to the correct
  // "will be flagged" hint. Approve has no such branch — it always sets a
  // real, immediately-counted status.
  // Migration 0025 (2026-09-10): PTO never needs a doctor's note — the rule
  // is for attendance absences, and the policy exempts PTO from points
  // altogether. Mirrors log_absence()'s own condition exactly.
  // 0031 (audit B4): the rule counts the WORKING days actually written, not the
  // calendar span — a Fri->Sun entry is one day and is not flagged. Same
  // closure set as the allotment estimate; advisory until log_absence() decides.
  const workingDays =
    startDate && endDate && endDate >= startDate
      ? estimatePtoDays(startDate, endDate, closures, 'Full')
      : startDate
        ? 1
        : 0;
  const unplanned = absenceType !== '' && absenceType !== 'PTO';
  const needsDocumentation =
    decision === 'Record' && unplanned && workingDays >= 2 && !doctorsNote;
  const unpaidWarning =
    absenceType === 'PTO' &&
    (decision === 'Approve' || (decision === 'Record' && !needsDocumentation)) &&
    startDate &&
    empRow
      ? unpaidWarningFor(
          employeeName,
          empRow.ptoTotal,
          empRow.allotment,
          estimatePtoDays(startDate, endDate || startDate, closures, ptoAmount),
        )
      : null;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!employeeName || !absenceType || !startDate) return;

    if (endDate && endDate < startDate) {
      setBanner({ kind: 'error', text: 'End date can’t be before start date.' });
      return;
    }

    const hours = lateHours.trim() === '' ? 0 : Number(lateHours);
    if (Number.isNaN(hours)) {
      setBanner({ kind: 'error', text: 'Late hours must be a number.' });
      return;
    }

    // Pre-P8 punch list, Tier 2: confirm before Approve/Deny, mirroring the
    // Dashboard's own wording — a firm, hard-to-walk-back decision email,
    // not the routine case. Record ALSO emails (a low-key FYI, already
    // disclosed in this form's intro copy) but deliberately gets no dialog
    // here — see submitLabel()'s comment for the reasoning and the
    // mitigation (naming who gets notified right on the button instead).
    if (decision === 'Approve' || decision === 'Deny') {
      const dateRangeText = endDate && endDate !== startDate ? `${startDate} → ${endDate}` : startDate;
      const verb = decision === 'Approve' ? 'Approve' : 'Deny';
      if (
        !window.confirm(
          `${verb} this ${absenceType} entry for ${employeeName} (${dateRangeText})? ${employeeName} will be emailed.`,
        )
      ) {
        return;
      }
    }

    setBusy(true);
    setBanner(null);

    const res = await logAbsence({
      employeeName,
      absenceType,
      startDate,
      endDate: endDate || null,
      decision,
      lateHours: hours,
      doctorsNote,
      notes: notes || null,
      ptoAmount,
    });

    setBusy(false);
    if (res.ok) {
      setBanner({ kind: 'success', text: res.message ?? 'Absence logged successfully.' });
      // Keep the employee selected (the common case is another entry for
      // someone else — but re-picking is one click either way); clear the rest.
      setAbsenceType('');
      setStartDate('');
      setEndDate('');
      setLateHours('');
      setDoctorsNote(false);
      setDecision('Record');
      setPtoAmount('Full');
      setNotes('');
      onLogged();
    } else {
      setBanner({
        kind: 'error',
        text: res.error ?? 'Something went wrong. Please try again.',
        detail: res.detail,
      });
    }
  }

  return (
    <div style={S.wrap}>
      {/* Accessibility-review fix (redesign Pass 2): AppShell no longer
          supplies a page heading of its own (nor did the old shared header
          give this view anything but a generic branding h1), so this page's
          own title is the document's real h1 now — matching the Dashboard
          view's own already-shipped pattern. */}
      <h1 style={{ ...S.title, ...(narrow ? S.titleNarrow : null) }}>Log an Absence</h1>
      <p style={S.sub}>
        Use this to record an absence that already happened (sick day, no-call-no-show, tardy) or
        to enter a PTO or time-off request. Unplanned absences are recorded; a PTO entry can also
        be approved, denied, or held as pending — Approve and Deny both email the employee, and
        Record only sends a low-key FYI by default too (Company Settings can turn that off).
        Anything other than PTO is also posted to the #absences Slack channel.
      </p>

      <form onSubmit={submit} style={{ ...S.card, ...(narrow ? S.cardNarrow : null) }}>
        <label htmlFor="la-employee" style={S.label}>
          Employee
        </label>
        <select
          id="la-employee"
          required
          value={employeeName}
          onChange={(e) => setEmployeeName(e.target.value)}
          style={S.input}
        >
          <option value="">Select employee</option>
          {employees.map((r) => (
            <option key={r.employee.id} value={r.employee.full_name}>
              {r.employee.full_name}
            </option>
          ))}
        </select>

        <label htmlFor="la-type" style={S.label}>
          Absence Type
        </label>
        <select
          id="la-type"
          required
          value={absenceType}
          onChange={(e) => {
            const next = e.target.value;
            setAbsenceType(next);
            // Pre-P8 punch list, Tier 3: the type-specific fields unmount
            // when the type changes, but their STATE did not — so hours typed
            // under Tardy/Late rode along invisibly and were sent with the
            // PTO entry logged next (log_absence stores late_hours on the
            // request regardless of type). Clear what the new type has no
            // field for.
            if (next !== 'Tardy/Late') setLateHours('');
            if (next !== 'PTO') setPtoAmount('Full');
            // 0025: the doctor's-note box is hidden for PTO, so a tick made
            // under another type must not ride along and be stored on the
            // PTO row (review finding — same shape as the two lines above).
            if (next === 'PTO') setDoctorsNote(false);
            // 0031 (B3): only PTO may be approved, denied or held Pending;
            // anything else is Record-only, so a decision picked under PTO
            // must not ride along (the RPC would refuse it).
            if (next !== 'PTO') setDecision('Record');
          }}
          style={S.input}
        >
          <option value="">Select type</option>
          {ABSENCE_TYPES.map((t) => (
            <option key={t} value={t}>
              {t}
            </option>
          ))}
        </select>

        {absenceType === 'PTO' && (
          <>
            <label htmlFor="la-amount" style={S.label}>
              PTO Amount
            </label>
            <select
              id="la-amount"
              value={ptoAmount}
              onChange={(e) => setPtoAmount(e.target.value === 'Half' ? 'Half' : 'Full')}
              aria-describedby="la-amount-hint"
              style={S.input}
            >
              <option value="Full">Full day</option>
              <option value="Half">Half day (0.5)</option>
            </select>
            {/* Same disclosure as the employee request form (pre-P8 punch
                list, Tier 3): a half day here is every day of the range.
                Always mounted, for the same live-region reason as there. */}
            <p
              id="la-amount-hint"
              role="status"
              aria-live="polite"
              style={halfDayRange ? S.halfDayHint : S.hintEmpty}
            >
              {halfDayRange
                ? `Half day will apply to every day from ${formatDayKey(startDate)} to ${formatDayKey(endDate)}. For a half day on just one of them, log that date on its own.`
                : ''}
            </p>
          </>
        )}

        <div style={S.row}>
          <div style={S.rowItem}>
            <label htmlFor="la-date" style={S.label}>
              Date
            </label>
            <input
              id="la-date"
              type="date"
              required
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              style={S.input}
            />
          </div>
          <div style={S.rowItem}>
            <label htmlFor="la-end" style={S.label}>
              End Date
            </label>
            <input
              id="la-end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              style={S.input}
            />
          </div>
        </div>

        {absenceType === 'Tardy/Late' && (
          <>
            <label htmlFor="la-hours" style={S.label}>
              If Late, how many hours?
            </label>
            <input
              id="la-hours"
              type="number"
              min={0}
              max={24}
              step={0.25}
              placeholder="Optional"
              value={lateHours}
              onChange={(e) => setLateHours(e.target.value)}
              style={S.input}
            />
          </>
        )}

        {/* A doctor's note has no meaning on PTO (0025) — offering the box
            there is what made the old flag look like a requirement. */}
        {absenceType !== 'PTO' && (
          <label style={{ ...S.label, ...S.checkboxLine }}>
            <input
              type="checkbox"
              checked={doctorsNote}
              onChange={(e) => setDoctorsNote(e.target.checked)}
            />
            Doctor&rsquo;s note provided
          </label>
        )}

        <label htmlFor="la-notes" style={S.label}>
          Notes
        </label>
        <textarea
          id="la-notes"
          rows={3}
          placeholder="Optional"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          style={{ ...S.input, resize: 'vertical' }}
        />

        <label htmlFor="la-decision" style={S.label}>
          Decision
        </label>
        {unplanned ? (
          // 0031 (B3): fixed, not a one-option select — there is no choice to make.
          <input
            id="la-decision"
            type="text"
            readOnly
            value="Record (unplanned absences are recorded, not approved)"
            style={S.input}
          />
        ) : (
          <select
            id="la-decision"
            required
            value={decision}
            onChange={(e) => setDecision(e.target.value as Decision)}
            style={S.input}
          >
            {DECISIONS.map((d) => (
              <option key={d.value} value={d.value}>
                {d.label}
              </option>
            ))}
          </select>
        )}

        {/* The Needs Documentation rule, said out loud before it happens
            rather than discovered in the result message. */}
        {needsDocumentation && (
          <p style={S.hint}>
            A 2+ day absence with no doctor&rsquo;s note will be flagged{' '}
            <strong>Needs Documentation</strong> for follow-up.
          </p>
        )}

        {/* Pre-P8 punch list, Tier 2: the Dashboard's own Approve button has
            carried this exact warning since v22; this on-the-spot path never
            did. Estimated from closure_days() + local weekday arithmetic,
            since this entry has no stored absence_days rows yet to read a
            real count from — advisory only, same as the Dashboard's. */}
        {unpaidWarning && <p style={S.hint}>{unpaidWarning}</p>}

        <button type="submit" disabled={busy} style={S.button}>
          {busy ? 'Logging…' : submitLabel(decision, employeeName, notifyEmployee)}
        </button>

        {banner && (
          <div style={banner.kind === 'error' ? S.error : S.success}>
            <p role={banner.kind === 'error' ? 'alert' : 'status'} style={S.bannerText}>
              {banner.text}
            </p>
            <TechnicalDetails detail={banner.detail} />
          </div>
        )}
      </form>
    </div>
  );
}

const S = {
  wrap: { maxWidth: '30rem', margin: '0 auto' },
  // Direction D pattern 1 (page title). Dashboard's own h1 runs 30px/23px
  // since it's the app's busiest screen; this is a secondary single-form
  // page, so it sits at the smaller 24px/20px pairing the redesign spec
  // calls out for exactly that case.
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 700,
    letterSpacing: '-0.01em',
    fontFamily: font.display,
    color: color.ink,
  },
  titleNarrow: { fontSize: '20px' },
  // Direction D pattern 2 (page subtitle).
  sub: {
    color: color.textMuted,
    fontSize: '14px',
    lineHeight: 1.5,
    marginTop: '4px',
    marginBottom: '1.25rem',
  },
  // Direction D pattern 3 (card container).
  card: {
    border: `1px solid ${color.cardBorder}`,
    borderRadius: radius.cardDesktop,
    padding: '26px 28px 22px',
    background: color.cardBg,
    boxShadow: shadow.card,
  },
  cardNarrow: { borderRadius: radius.cardMobile, padding: '16px', boxShadow: 'none' },
  label: {
    display: 'block',
    fontFamily: font.body,
    fontSize: '0.85rem',
    fontWeight: 600,
    color: color.ink,
    marginBottom: '0.4rem',
  },
  checkboxLine: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    marginBottom: '1rem',
    cursor: 'pointer',
  },
  // Direction D pattern 13 (form inputs) — the one pattern the style guide
  // established fresh here, since neither theme.ts nor Dashboard.tsx has a
  // prior text-input example. 1rem/16px stays exactly as-is: stops iOS
  // Safari's focus auto-zoom (RequestTimeOff.tsx's S.input has the story),
  // and this pass must not regress that already-fixed bug.
  input: {
    width: '100%',
    boxSizing: 'border-box',
    fontFamily: font.body,
    fontSize: '1rem',
    padding: '0.5rem 0.7rem',
    border: `1px solid ${color.cardBorder}`,
    borderRadius: '0.6rem',
    marginBottom: '1rem',
    background: color.cardBg,
    color: color.ink,
  },
  // The two date fields sit side by side when they fit and stack when they
  // don't: at 16px (item 8) a date input's intrinsic width is ~163px, and two
  // of them plus the gap overflowed a 375px page by 21px — caught live.
  row: { display: 'flex', gap: '0.9rem', flexWrap: 'wrap' },
  rowItem: { flex: '1 1 10rem', minWidth: 0 },
  // Direction D pattern 8 (warning/info banner) — covers both the
  // Needs-Documentation heads-up and the unpaid-allotment warning, which
  // already shared this one style before the reskin.
  hint: {
    margin: '0 0 0.55rem',
    padding: '0.45rem 0.7rem',
    borderRadius: '0.6rem',
    background: color.amberBg,
    color: color.amberDark,
    border: `1px solid ${color.amberBorder}`,
    fontSize: '0.82rem',
    lineHeight: 1.45,
  },
  // Same box as `hint`, pulled up under the PTO Amount select's own 1rem
  // bottom margin so it reads as attached to it (matches RequestTimeOff).
  halfDayHint: {
    margin: '-0.4rem 0 1rem',
    padding: '0.45rem 0.7rem',
    borderRadius: '0.6rem',
    background: color.amberBg,
    color: color.amberDark,
    border: `1px solid ${color.amberBorder}`,
    fontSize: '0.82rem',
    lineHeight: 1.45,
  },
  hintEmpty: { margin: 0 },
  // Direction D pattern 5 (primary action button).
  button: {
    width: '100%',
    padding: '0.55rem 1.1rem',
    minHeight: '2.75rem',
    fontSize: '0.9rem',
    fontWeight: 700,
    fontFamily: font.display,
    color: 'white',
    background: color.brandBlue,
    border: 0,
    borderRadius: radius.pill,
    cursor: 'pointer',
  },
  bannerText: { margin: 0 },
  // Result banner, success case — no named Direction D pattern covers this
  // (8/9 are warning/error only), so it borrows the same shape and reuses
  // the theme's teal token rather than inventing a new hex.
  success: {
    marginTop: '1rem',
    marginBottom: 0,
    padding: '0.45rem 0.7rem',
    borderRadius: '0.6rem',
    background: color.chipTealBg,
    color: color.teal,
    fontSize: '0.87rem',
    lineHeight: 1.5,
  },
  // Direction D pattern 9 (error banner) — the established '#fdecef'/
  // '#b00020' pair, verbatim.
  error: {
    marginTop: '1rem',
    marginBottom: 0,
    padding: '0.45rem 0.7rem',
    borderRadius: '0.6rem',
    background: '#fdecef',
    color: '#b00020',
    // Cross-file consistency review: matches the border hex ManageRecords.tsx
    // already had (its own pre-existing voidBtn border), rather than the
    // brand-new #f3c6cf this file and AdminConsole.tsx had each separately
    // invented for the same error-banner role.
    border: '1px solid #e5b3bb',
    fontSize: '0.87rem',
    lineHeight: 1.5,
  },
} satisfies Record<string, React.CSSProperties>;
